import { useMutation } from '@tanstack/react-query'

import { type FileKey, fileContentTypeSchema, MAX_FILE_SIZE_BYTES } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Two-step file upload: gets a presigned S3 URL from the backend,
 * then uploads the file directly to S3.
 * Returns the file metadata to include in entity creation bodies.
 */
export function useUploadFile() {
  const api = useOsApi()

  return useMutation({
    mutationFn: async (file: File): Promise<FileKey> => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error(`File exceeds the ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB size limit`)
      }

      const contentType = fileContentTypeSchema.safeParse(file.type)
      if (!contentType.success) {
        throw new Error(`"${file.name}" cannot be attached — this file type is not supported`)
      }

      // Step 1: Get presigned upload URL from backend
      const response = await api.post('/files/upload-url', {
        body: { fileName: file.name, contentType: contentType.data },
      })
      if (!response.success) throw new Error(response.error.message)
      const { uploadUrl, s3Key } = response.data

      // Step 2: Upload file directly to S3 (raw fetch with the file's content type)
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      return {
        s3Key,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }
    },
  })
}
